"use client";

import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { A11y } from "swiper/modules";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "swiper/css";
import "swiper/css/a11y";
const services = [{
  id: 1,
  title: "Fundamenty",
  stage: "01",
  description: "Wykonujemy tradycyjne ławy i ściany fundamentowe z precyzyjną izolacją przeciwwilgociową. Przygotowujemy stabilną podstawę pod każdy rodzaj konstrukcji ściennej.",
  image: "https://budsys.pl/wp-content/uploads/2026/09/IMG_4932.jpeg?v=95w405cfz7o",
  srcset: "https://budsys.pl/wp-content/uploads/2026/09/IMG_4932.jpeg?v=ff8jeat6it-wvc-srcset",
  sizes: "https://budsys.pl/wp-content/uploads/2026/09/IMG_4932.jpeg?v=4v0tcp806yp-wvc-sizes",
  alt: "Zbrojenie i betonowanie tradycyjnych ław fundamentowych"
}, {
  id: 2,
  title: "Płyty fundamentowe",
  stage: "02",
  description: "Realizujemy zbrojone monolityczne płyty fundamentowe z pełną izolacją termiczną od spodu. Rozwiązanie zapewnia równomierne osiadanie budynku nawet na trudnym gruncie.",
  image: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5216-scaled.jpeg?v=g4xtvhoco8v",
  srcset: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5216-scaled.jpeg?v=vv08tmr9e5k-wvc-srcset",
  sizes: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5216-scaled.jpeg?v=q31d4r46t5-wvc-sizes",
  alt: "Wylewanie i zacieranie monolitycznej płyty fundamentowej"
}, {
  id: 3,
  title: "Murowanie",
  stage: "03",
  description: "Wznosimy ściany nośne i działowe z pustaków ceramicznych, silikatów oraz betonu komórkowego. Dbamy o równe spoiny i prawidłowe wiązania murarskie.",
  image: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5408-scaled.jpeg?v=s0rcszaieql",
  srcset: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5408-scaled.jpeg?v=g1vxeha3i3o-wvc-srcset",
  sizes: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5408-scaled.jpeg?v=b3laf1bqmsu-wvc-sizes",
  alt: "Precyzyjne murowanie ścian konstrukcyjnych budynku"
}, {
  id: 4,
  title: "Stropy",
  stage: "04",
  description: "Montujemy i betonujemy stropy gęstożebrowe, prefabrykowane oraz monolityczne płyty żelbetowe. Prace prowadzimy zgodnie ze sztuką inżynieryjną i projektem wykonawczym.",
  image: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5551-scaled.jpeg?v=k5547ma5gc",
  srcset: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5551-scaled.jpeg?v=juptxk2bs6-wvc-srcset",
  sizes: "https://budsys.pl/wp-content/uploads/2026/09/IMG_5551-scaled.jpeg?v=qumis61p4p-wvc-sizes",
  alt: "Układanie zbrojenia i szalowanie stropu żelbetowego"
}, {
  id: 5,
  title: "Przyłącza wody, prądu i kanalizacji",
  stage: "05",
  description: "Realizujemy wykopy liniowe, wpusty instalacyjne oraz przepusty przez ławy fundamentowe. Przygotowujemy kompletną infrastrukturę do podłączenia mediów zewnętrznych.",
  image: "https://budsys.pl/wp-content/uploads/2026/09/95770e63-97c8-4376-977a-2522315c0c35-high.webp?v=p43ffj9yhj",
  srcset: "https://budsys.pl/wp-content/uploads/2026/09/95770e63-97c8-4376-977a-2522315c0c35-high.webp?v=b0jj270kij-wvc-srcset",
  sizes: "https://budsys.pl/wp-content/uploads/2026/09/95770e63-97c8-4376-977a-2522315c0c35-high.webp?v=ro3eognjwkd-wvc-sizes",
  alt: "Wykopy pod przyłącza mediów budowlanych i kanalizację"
}];
function SwiperNavControls() {
  const swiper = useSwiper();
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  useEffect(() => {
    const updateNavState = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    swiper.on("slideChange", updateNavState);
    updateNavState();
    return () => {
      swiper.off("slideChange", updateNavState);
    };
  }, [swiper]);
  return <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" aria-label="Poprzedni etap" disabled={isBeginning} onClick={() => swiper.slidePrev()} className="size-10 rounded-md border-border bg-card text-foreground transition-colors hover:border-accent hover:text-foreground">
        <ArrowLeft className="size-4" />
      </Button>
      <Button variant="outline" size="icon" aria-label="Następny etap" disabled={isEnd} onClick={() => swiper.slideNext()} className="size-10 rounded-md border-border bg-card text-foreground transition-colors hover:border-accent hover:text-foreground">
        <ArrowRight className="size-4" />
      </Button>
    </div>;
}
export default function Housebuilding() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="budowa-domów" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Section Header */}
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            {/* Operational Meta Pill */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            ease: "easeOut"
          }} className="mb-4 inline-flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-border bg-secondary/70 px-3.5 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
                <span className="size-1.5 rounded-full bg-accent" />
                ZASIĘG DO 25 KM
              </Badge>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="size-3.5 text-accent" />
                Bielsko-Biała i okolice w promieniu ok. 25 km
              </span>
            </motion.div>

            <motion.h2 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: 0.05,
            ease: "easeOut"
          }} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Budowa domów do stanu surowego
            </motion.h2>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: 0.1,
            ease: "easeOut"
          }} className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Wznosimy konstrukcje domów jednorodzinnych ze sprawdzonych materiałów.
              Zapewniamy stały nadzór wykonawczy i dokładność każdego etapu, od
              pierwszego wykopu po zamknięcie wieńca stropowego.
            </motion.p>
          </div>

          <div className="hidden md:block">
            <span className="text-xs font-mono tracking-widest text-muted-foreground uppercase">
              5 ETAPÓW REALIZACJI
            </span>
          </div>
        </div>

        {/* Architectural Hairline Datum Divider */}
        <div className="my-10 h-px w-full bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* Horizontal Swiper Rail */}
        <Swiper modules={[A11y]} spaceBetween={24} slidesPerView={1.15} breakpoints={{
        640: {
          slidesPerView: 2.1,
          spaceBetween: 24
        },
        1024: {
          slidesPerView: 3.1,
          spaceBetween: 24
        },
        1280: {
          slidesPerView: 3.3,
          spaceBetween: 28
        }
      }} a11y={{
        prevSlideMessage: "Poprzedni etap budowy",
        nextSlideMessage: "Następny etap budowy"
      }} className="overflow-visible pb-4">
          {services.map((service, i) => <SwiperSlide key={service.id} data-index={i} className="h-auto">
              <Card className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border/80 bg-card/80 p-0 shadow-sm backdrop-blur-md transition-[border-color,box-shadow] duration-250 hover:border-accent hover:shadow-md">
                {/* Visual Image Block */}
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {/* Subtle Scrim for Depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60" />

                  {/* Stage Label */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-md bg-background/90 px-2.5 py-1 text-xs font-mono font-medium text-foreground backdrop-blur-sm">
                    <span>ETAP</span>
                    <span className="font-bold text-accent">{service.stage}</span>
                  </div>
                </div>

                {/* Card Content */}
                <CardContent className="flex flex-1 flex-col justify-between p-6">
                  <div>
                    <h3 className="font-default text-xl font-bold tracking-tight text-foreground group-hover:text-primary">
                      {service.title}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                      {service.description}
                    </p>
                  </div>

                  {/* Micro Architectural Footer Detail */}
                  <div className="mt-6 flex items-center gap-2 border-t border-border/60 pt-4 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                    <span>Zgodnie z dokumentacją budowlaną</span>
                  </div>
                </CardContent>
              </Card>
            </SwiperSlide>)}

          {/* Slider Controls Footer */}
          <div className="mt-8 flex items-center justify-between border-t border-border/60 pt-6">
            <p className="text-xs font-mono text-muted-foreground">
              Przesuń palcem lub użyj nawigacji, aby poznać wszystkie etapy
            </p>
            <SwiperNavControls />
          </div>
        </Swiper>
      </div>
    </section>;
}